import React from 'react';
import { PageProps, graphql } from 'gatsby';

import Layout from '../../components/layouts/Layout';
import PhoneScreen from '../../components/screens/PhoneScreen/PhoneScreen';
import { StoreLocatorProvider } from '../../components/StoreLocator';
import { CMSPageData, PageSection } from '../../ContentfulTypes';
import { GatsbyPageContextProps } from '../../types';
import variables from '../../styles/variables';

const Phones = ({
  pageContext,
  data,
  location,
  preferredProvince,
}: PageProps<CMSPageData, GatsbyPageContextProps>) => {
  // Find page section for allPhones
  const allPhones = data.contentfulPage?.pageSections.find(
    ({ contentful_id: id }) => id === 'MH24ZpZiPZ6NGES0bJm0a',
  ) as PageSection;
  // Find page section for allTablets
  const allTablets = data.contentfulPage?.pageSections.find(
    ({ contentful_id: id }) => id === '4I45cXXctKYE0FfPYe0p7H',
  ) as PageSection;

  const allPrepaidPhones = data.contentfulPage?.pageSections.find(
    ({ contentful_id: id }) => id === '49SUnsCmnW6hIwViCXcb2X',
  ) as PageSection;

  // Add tablets to the end of the phones list
  allPhones?.phones?.push(...allPrepaidPhones.phones, ...allTablets.phones);

  // Filter out duplicates
  const allUrlSlugs = allPhones?.phones?.map((phone) => phone.urlSlug);
  allPhones.phones = allPhones?.phones?.filter(
    (phone, index) => allUrlSlugs?.indexOf(phone.urlSlug) === index,
  );

  return (
    <StoreLocatorProvider>
      <Layout
        locale={pageContext.locale}
        location={location}
        backgroundColor={variables.colorGrey2}
      >
        <PhoneScreen
          locale={pageContext.locale}
          preferredProvince={preferredProvince}
          location={location}
          contentfulData={data.contentfulPage}
          metaTitle={data.contentfulPage?.metaTitle}
          metaDescription={data.contentfulPage?.metaDescription?.metaDescription}
          allPhonesData={allPhones}
          phoneHeading={allPhones?.sectionHeading}
          tabletHeading={allTablets?.sectionHeading}
          prepaidPhonesHeading={allPrepaidPhones?.sectionHeading}
          phoneBrands={data?.allContentfulPhoneBrand}
        />
      </Layout>
    </StoreLocatorProvider>
  );
};

// ts-prune-ignore-next
export const query = graphql`
  query phonePageData($formattedLocale: String = "en-CA") {
    contentfulPage(
      contentful_id: {eq: "6eRIi6hyF1tdyjvn8V8Xji"}
      node_locale: {eq: $formattedLocale}
    ) {
      metaTitle
      metaDescription {
        metaDescription
      }
      contentful_id
      node_locale
      pageSlug
      pageSections {
        ...MultiHeroBannerQuery
        ... on ContentfulPhonesSection {
          __typename
          contentful_id
          sectionHeading
          phones {
            ...LitePhoneQuery
          }
        }
        ... on ContentfulConditionalSection {
          __typename
          id
          contentful_id
          sectionContent {
            ...MultiHeroBannerQuery
            ...ContentfulBannerQuery
          }
        }
        ...ImageSectionQuery
        ... on ContentfulForm {
          __typename
          id
          contentful_id
          formFields {
            contentful_id
            displayLabel
            placeholderLabel
          }
        }
        ... on ContentfulTextSection {
          __typename
          contentful_id
          heading {
            raw
          }
          contentBody {
            raw
            references {
              ...ContentfulCtaQuery
            }
          }
        }
        ... on ContentfulCombinatorSection {
          __typename
          contentful_id
          combinatorSectionItems {
            ...MultiHeroBannerQuery
            ...ContentfulBannerQuery
            ... on ContentfulCardItem {
              __typename
              id
              contentful_id
              cardItemHideOption
              cmsName
              cardIcon {
                ...DamAssetQuery
              }
              cardHeading
              cardCta {
                ...ContentfulCtaQuery
              }
            }
            ... on ContentfulTextSection {
              __typename
              contentful_id
              heading {
                raw
              }
              contentBody {
                raw
                references {
                  ...ContentfulCtaQuery
                }
              }
            }
            ... on ContentfulMobilistTip {
              contentful_id
              mobilistName
              node_locale
              tipHeading
              tipSubHeading
              tipBody {
                raw
              }
              tipCta {
                ... on ContentfulCta {
                  ...ContentfulCtaQuery
                }
              }
              mobilistImage {
                altText
                asset {
                  url
                  localImage {
                    id
                    publicURL
                    childImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentfulFilterModule {
          __typename
          contentful_id
          filterGroups {
            contentful_id
            cmsName
            displayLabel
            filterCategories {
              cmsName
              contentful_id
              displayLabel
              filterTags {
                contentful_id
                cmsName
                displayLabel
              }
            }
          }
          filterLabels {
            contentful_id
            cmsName
            ... on ContentfulForm {
              __typename
              id
              formFields {
                __typename
                displayLabel
                fieldType
                fieldName
                contentful_id
              }
            }
          }
          filterBlade {
            contentful_id
            ... on ContentfulBlade {
              __typename
              contentful_id
              bladeTitle
              bladeCloseLabel
              bladeName
              bladeSections {
                ... on ContentfulKeyValuePair {
                  __typename
                  contentful_id
                  key
                  value
                }
                ... on ContentfulTextSection {
                  __typename
                  contentful_id
                  heading {
                    raw
                  }
                  subHeading {
                    raw
                  }
                }
                ... on ContentfulForm {
                  __typename
                  contentful_id
                  id
                  formFields {
                    displayLabel
                    placeholderLabel
                    errorMessage
                  }
                }
                ...ContentfulCtaQuery
                ... on ContentfulCardItem {
                  __typename
                  contentful_id
                  id
                  contentful_id
                  cardFlag
                  cardHeading
                  cardIcon {
                    ...DamAssetQuery
                  }
                  cardDescription {
                    cardDescription
                  }
                  cardCta {
                    ctaTarget
                    ctaUrl
                    ctaType
                    ctaAriaLabel
                    ctaColor
                    ctaLabel {
                      ctaLabel
                    }
                    ctaIconDam {
                      ...DamAssetQuery
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentfulMsaModule {
          id
          mobilistImage {
            ...DamAssetQuery
          }
          tipImage {
            ...DamAssetQuery
          }
          msaSteps {
            contentful_id
            cmsName
            dialogueText
            tipText
            allowMultipleChoices
            stepCtas {
              ...ContentfulCtaQuery
            }
            progressImage {
              ...DamAssetQuery
            }
            msaStepChoices {
              description {
                raw
              }
              title
              choiceIcon {
                ...DamAssetQuery
                title
              }
              smartFilter {
                cmsName
                contentful_id
                displayLabel
              }
            }
            smartFilter {
              contentful_id
              cmsName
            }
          }
          contentful_id
          selectAllLabel
          phonesFoundLabel
          stepChoiceSelectionError
          noPhonesFoundError
          msaMatchMadeStep {
            dialogueText
            contentful_id
            stepCtas {
              ...ContentfulCtaQuery
            }
            description {
              description
            }
          }
          msaPauseStep {
            contentful_id
            dialogueText
            stepCtas {
              ...ContentfulCtaQuery
            }
          }
        }
        ... on ContentfulTradeInModule {
          ...PhobioQuery
        }
      }
    }
    ...AllPhoneBrandsQuery
  }
`;

export default Phones;
